/**
 * Progress report arithmetic, shared by the coach's comparison sheet (#245) and
 * the client's own progress view (#232).
 *
 * Everything here is pure. The views import it and render; the arithmetic that
 * can silently lie about a client's clinical data — parsing a decimal that
 * arrives as a string, deciding what a missing weigh-in means, turning an
 * answer into a severity — lives here where it can be tested directly.
 *
 * It lives in the shared package rather than in either app because the two
 * views must not be able to disagree. If the client's chart said "always
 * thankful" was a bad week while the coach's sheet said it was a good one, one
 * of them would be wrong and nothing would say which. There is one direction
 * table, and both apps read it.
 */
export const SYMPTOM_FREQUENCIES = [
    'NEVER',
    'RARELY',
    'SOMETIMES',
    'FREQUENTLY',
    'ALWAYS',
];
/**
 * The words the client saw when they answered. Deliberately the same strings as
 * apps/client/src/types/progress-reports.ts: a coach reading "Often" while the
 * client chose "Frequently" is a needless way to misread a clinical answer.
 */
export const FREQUENCY_LABELS = {
    NEVER: 'Never',
    RARELY: 'Rarely',
    SOMETIMES: 'Sometimes',
    FREQUENTLY: 'Often',
    ALWAYS: 'Always',
};
/** The 11 physical wellness rows, in the order the paper form asks them. */
export const PHYSICAL_ROWS = [
    { key: 'troubleFallingAsleep', label: 'Trouble falling asleep', direction: 'higher-is-worse' },
    { key: 'wakeDuringNight', label: 'Waking during the night', direction: 'higher-is-worse' },
    {
        key: 'needCaffeineToGetGoing',
        label: 'Needing caffeine to get going',
        direction: 'higher-is-worse',
    },
    { key: 'mentalFog', label: 'Mental fog', direction: 'higher-is-worse' },
    {
        key: 'sinusitisOrPostNasalDrip',
        label: 'Sinusitis or post-nasal drip',
        direction: 'higher-is-worse',
    },
    {
        key: 'heartburnOrAcidReflux',
        label: 'Heartburn or acid reflux',
        direction: 'higher-is-worse',
    },
    { key: 'bloatingOrFlatulence', label: 'Bloating or flatulence', direction: 'higher-is-worse' },
    { key: 'constipation', label: 'Constipation', direction: 'higher-is-worse' },
    { key: 'diarrhoea', label: 'Diarrhoea', direction: 'higher-is-worse' },
    { key: 'headachesOrMigraines', label: 'Headaches or migraines', direction: 'higher-is-worse' },
    { key: 'otherPain', label: 'Other pain', direction: 'higher-is-worse' },
];
/** The 8 emotion rows. Note the direction flip from `content` onwards. */
export const EMOTION_ROWS = [
    { key: 'anxious', label: 'Anxious', direction: 'higher-is-worse' },
    { key: 'depressed', label: 'Depressed', direction: 'higher-is-worse' },
    { key: 'stressed', label: 'Stressed', direction: 'higher-is-worse' },
    { key: 'content', label: 'Content', direction: 'higher-is-better' },
    { key: 'peacefulAndCalm', label: 'Peaceful and calm', direction: 'higher-is-better' },
    {
        key: 'compassionWithMyself',
        label: 'Compassionate with myself',
        direction: 'higher-is-better',
    },
    { key: 'thankfulForMyLife', label: 'Thankful for my life', direction: 'higher-is-better' },
    {
        key: 'optimisticAboutMyProgress',
        label: 'Optimistic about my progress',
        direction: 'higher-is-better',
    },
];
/**
 * The rows that are symptoms, taken from the direction table rather than
 * hand-listed.
 *
 * "Thankful for my life" is a frequency row and is not a symptom, so a count of
 * symptoms has to exclude it and the four other `higher-is-better` emotion
 * rows. Deriving the list from `direction` instead of writing out fourteen keys
 * means adding a row to either table cannot leave this behind.
 */
export const SYMPTOM_ROWS = [...PHYSICAL_ROWS, ...EMOTION_ROWS].filter((row) => row.direction === 'higher-is-worse');
/** Every frequency row, physical then emotional, in form order. */
export const ALL_FREQUENCY_ROWS = [...PHYSICAL_ROWS, ...EMOTION_ROWS];
export const SCALE_ROWS = [
    { key: 'energyLevel', label: 'Energy level', max: 10, direction: 'higher-is-better' },
    { key: 'sleepQuality', label: 'Sleep quality', max: 10, direction: 'higher-is-better' },
    { key: 'painLevel', label: 'Pain level', max: 10, direction: 'higher-is-worse' },
    { key: 'commitmentLevel', label: 'Commitment', max: 5, direction: 'higher-is-better' },
];
/** Position in the scale, NEVER = 0 through ALWAYS = 4. Not severity. */
export function frequencyRank(frequency) {
    return SYMPTOM_FREQUENCIES.indexOf(frequency);
}
export function concernOfFrequency(frequency, direction) {
    const rank = frequencyRank(frequency);
    return (direction === 'higher-is-worse' ? rank : 4 - rank);
}
/**
 * A 1-n scale mapped onto the same 0-4 severity, so a table mixing Likert rows
 * with numeric ones reads as one scale rather than two.
 */
export function concernOfScale(value, max, direction) {
    if (!Number.isFinite(value))
        return 0;
    const clamped = Math.min(Math.max(value, 1), max);
    const badness = direction === 'higher-is-worse' ? (clamped - 1) / (max - 1) : (max - clamped) / (max - 1);
    return Math.round(badness * 4);
}
// ---------------------------------------------------------------------------
// Weight
//
// The headline number, nullable by decision M, and arriving as a string. Every
// failure mode the work package names lives in these four functions.
// ---------------------------------------------------------------------------
/**
 * The Decimal string to a number, or null when there was no weigh-in.
 *
 * `Number` rather than `parseFloat` on purpose: parseFloat('159.7kg') is 159.7,
 * which turns a corrupt payload into a plausible clinical reading. Number gives
 * NaN and we return null. The empty string is guarded explicitly because
 * Number('') is 0, and 0 kg is the exact lie this must never tell.
 */
export function parseWeightKg(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'number')
        return Number.isFinite(raw) ? raw : null;
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}
/** One decimal place, because that is what the scale reads. Never "0" for absent. */
export function formatWeightKg(kg) {
    return kg === null ? '—' : kg.toFixed(1);
}
/** Signed to one decimal, so a loss reads as a loss at a glance. */
export function formatWeightDelta(delta) {
    if (delta === null)
        return '';
    if (Math.abs(delta) < 0.05)
        return '0.0';
    return `${delta > 0 ? '+' : '−'}${Math.abs(delta).toFixed(1)}`;
}
export function weightPoints(reports) {
    let previous = null;
    return reports.map((report, index) => {
        const kg = parseWeightKg(report.weightKg);
        const deltaFromPreviousWeighIn = kg !== null && previous !== null ? kg - previous : null;
        if (kg !== null)
            previous = kg;
        return { index, kg, deltaFromPreviousWeighIn };
    });
}
/**
 * Contiguous runs of items that have a value, split wherever one does not.
 *
 * This is the whole answer to #278, and it is deliberately the ONLY place the
 * split is implemented. A chart renders one path per segment, so there is never
 * path data spanning a gap for the renderer to interpolate across — and, more
 * to the point, never a `L281.3,undefined` for the SVG parser to stop dead at,
 * silently dropping every reading that came after it.
 *
 * Generic because the client charts three series (weight, energy, sleep) and
 * only one of them is nullable today. Routing all three through one function
 * means the nullable case cannot be the one that got the untested code path.
 *
 * A lone value sits in a segment of length 1 and is drawn as a dot with no line.
 */
export function segmentSeries(items, hasValue) {
    const segments = [];
    let current = [];
    for (const item of items) {
        if (hasValue(item)) {
            current.push(item);
        }
        else if (current.length > 0) {
            segments.push(current);
            current = [];
        }
    }
    if (current.length > 0)
        segments.push(current);
    return segments;
}
/** Contiguous runs of recorded weights. A missing weigh-in ends the run. */
export function weightSegments(points) {
    return segmentSeries(points, (point) => point.kg !== null);
}
export function summariseWeight(points) {
    const recorded = points.filter((p) => p.kg !== null);
    const values = recorded.map((p) => p.kg);
    return {
        recordedCount: recorded.length,
        missingCount: points.length - recorded.length,
        first: values.at(0) ?? null,
        last: values.at(-1) ?? null,
        change: values.length >= 2 ? values[values.length - 1] - values[0] : null,
        min: values.length > 0 ? Math.min(...values) : null,
        max: values.length > 0 ? Math.max(...values) : null,
    };
}
/**
 * The direction flip, applied once.
 *
 * A rise in "energy" and a rise in "anxious" are the same arithmetic and
 * opposite news. This is the only place that decides which.
 */
export function verdictOf(delta, direction) {
    if (delta === null)
        return 'none';
    if (direction === 'neutral')
        return 'none';
    if (Math.abs(delta) < 0.05)
        return 'flat';
    const rising = delta > 0;
    if (direction === 'higher-is-better')
        return rising ? 'better' : 'worse';
    return rising ? 'worse' : 'better';
}
/**
 * One metric's story across a run of reports.
 *
 * Takes the values already extracted rather than the reports, because weight
 * arrives as a Decimal string and the two scales arrive as numbers, and the
 * caller has already had to deal with that. Nulls are dropped rather than
 * treated as zero: a client who did not weigh in has no reading, and a reading
 * of 0 kg is the exact lie the whole module exists to avoid.
 *
 * With fewer than two recorded values `delta` is null and the verdict is
 * 'none'. That is the single-report case and the never-recorded case, and both
 * have to render as "nothing to compare yet" rather than as a change of 0.
 */
export function changeIn(values, direction) {
    const recorded = values.filter((value) => value !== null);
    const first = recorded.length > 0 ? recorded[0] : null;
    const last = recorded.length > 0 ? recorded[recorded.length - 1] : null;
    const delta = recorded.length >= 2 ? recorded[recorded.length - 1] - recorded[0] : null;
    return {
        first,
        last,
        delta,
        recordedCount: recorded.length,
        missingCount: values.length - recorded.length,
        verdict: verdictOf(delta, direction),
    };
}
/**
 * The frequencies that count as carrying a symptom.
 *
 * The line is drawn between "Sometimes" and "Often" because that is where the
 * form's own wording stops describing an occasional event and starts
 * describing a state the client is living in.
 */
export const HIGH_FREQUENCIES = ['FREQUENTLY', 'ALWAYS'];
export function isHighFrequency(frequency) {
    return HIGH_FREQUENCIES.includes(frequency);
}
/**
 * How many of `rows` the client answered Often or Always on one report.
 *
 * `rows` must share a direction. All three exported groups do by construction,
 * since each is filtered on it — and a mixed set would make the number
 * meaningless, because "3" would be three of two opposite things.
 */
export function countHighIn(report, rows) {
    return rows.filter((row) => isHighFrequency(report[row.key])).length;
}
/**
 * How many symptoms the client reported as Often or Always on one report.
 *
 * Counted over `SYMPTOM_ROWS` — the fourteen `higher-is-worse` rows — and not
 * over all nineteen. Running the five positive emotion rows through
 * `concernOfFrequency` first would fold "never thankful for my life" into the
 * same number, and the headline this feeds says "symptoms you reported as
 * Often or Always", which would then be a false statement about what the
 * client actually ticked. The positives are counted separately, by
 * `POSITIVE_FEELING_ROWS`, where a high count is good news.
 */
export function countHighSymptoms(report) {
    return countHighIn(report, SYMPTOM_ROWS);
}
/**
 * The single most compelling line available from this data: how many symptoms
 * the client was living with then, and how many now.
 *
 * A count, not a score. Nothing is weighted, nothing is invented, and a coach
 * can check it against the sheet by eye.
 *
 * Null when there are no reports at all. With exactly one report `first` and
 * `last` are that report and `delta` is null — a starting point, not a change.
 */
export function symptomCountChange(reports) {
    return countChangeIn(reports, SYMPTOM_ROWS);
}
/**
 * The same count over any one group of rows, first report to last.
 *
 * The verdict comes from the ROWS' own direction, and that is the whole reason
 * this is one function rather than two. Over `SYMPTOM_ROWS` a rising count is
 * bad news; over `POSITIVE_FEELING_ROWS` — where the client is telling you they
 * feel thankful, content and optimistic more often — the identical arithmetic
 * is good news. Writing the verdict out by hand at each call site is exactly
 * how the sheet would end up flagging a client getting better.
 *
 * `rows` must share a direction; see `countHighIn`.
 */
export function countChangeIn(reports, rows) {
    if (reports.length === 0 || rows.length === 0)
        return null;
    const first = countHighIn(reports[0], rows);
    const last = countHighIn(reports[reports.length - 1], rows);
    const delta = reports.length >= 2 ? last - first : null;
    return {
        first,
        last,
        delta,
        outOf: rows.length,
        verdict: verdictOf(delta, rows[0].direction),
    };
}
/**
 * The counts as a series, one per report, for a sparkline.
 *
 * Never null: a report the client filed always has an answer in every row, so
 * unlike weight there is no such thing as a missing count.
 */
export function countSeriesIn(reports, rows) {
    return reports.map((report) => countHighIn(report, rows));
}
/**
 * The three emotion rows a client wants LESS of, and the five they want more of.
 *
 * Split on direction rather than by hand for the same reason `SYMPTOM_ROWS` is:
 * adding a row to `EMOTION_ROWS` must not be able to leave either group behind,
 * and the split is the thing that stops "always anxious" and "always thankful"
 * being counted as the same news.
 */
export const DIFFICULT_FEELING_ROWS = EMOTION_ROWS.filter((row) => row.direction === 'higher-is-worse');
export const POSITIVE_FEELING_ROWS = EMOTION_ROWS.filter((row) => row.direction === 'higher-is-better');
/** The 11 physical rows. Every one of them is `higher-is-worse`. */
export const PHYSICAL_SYMPTOM_ROWS = PHYSICAL_ROWS.filter((row) => row.direction === 'higher-is-worse');
/**
 * What the client has earned by turning up, and nothing else.
 *
 * Every one of these is about the ACT of reporting — one sent, four sent, a
 * month of them — and none is about a number moving the right way. That is
 * deliberate and it is the hard part of the feature: a client whose weight went
 * up and whose symptoms got worse still filed four reports, and telling them
 * that is honest. Congratulating them on progress they did not make is not.
 *
 * Returns them oldest first, so a view can show the most recent by taking the
 * last one.
 */
export function milestonesFor(reports) {
    if (reports.length === 0)
        return [];
    const earned = [];
    earned.push({
        key: 'first-report',
        title: 'First report sent',
        detail: 'You started tracking. Everything on this page is measured from here.',
    });
    if (reports.length >= 4) {
        earned.push({
            key: 'fourth-report',
            title: 'Four reports sent',
            detail: `You have sent ${reports.length} reports. That is enough for a trend rather than a snapshot.`,
        });
    }
    if (spansAMonth(reports)) {
        earned.push({
            key: 'first-month',
            title: 'A month of tracking',
            detail: 'Your first report and your latest are more than a month apart.',
        });
    }
    return earned;
}
/**
 * Whether the run covers at least a month of real time.
 *
 * Measured between the first and last REPORT DATE, not by counting reports: a
 * client who filed four in one week has not tracked for a month, and one who
 * filed two five weeks apart has.
 *
 * A calendar month rather than a fixed number of days, because the milestone
 * says "more than a month apart" and 28 or 30 days would make that sentence
 * false in most months. `setMonth` rolls a 31st over into the next month — 31
 * January reads as 3 March — which errs towards making the client wait, and
 * that is the right way for a milestone to be wrong.
 *
 * An unparseable date returns false rather than NaN, so a bad row cannot hand
 * out a milestone.
 */
function spansAMonth(reports) {
    const first = new Date(reports[0].reportDate);
    const last = new Date(reports[reports.length - 1].reportDate);
    if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime()))
        return false;
    const aMonthOn = new Date(first);
    aMonthOn.setMonth(aMonthOn.getMonth() + 1);
    return last.getTime() >= aMonthOn.getTime();
}
// ---------------------------------------------------------------------------
// Column selection
// ---------------------------------------------------------------------------
/**
 * How many reports the sheet shows at once. Six is what fits one A4 landscape
 * sheet at a legible size, and printing one sheet is the point of the view.
 */
export const COLUMN_LIMITS = [4, 6, 8];
export const DEFAULT_COLUMN_LIMIT = 6;
/**
 * The most recent `limit` reports, still in ascending order.
 *
 * Reports arrive ordered by periodStart and are shown left to right oldest
 * first, so trimming takes from the front. A coach comparing the last six weeks
 * wants weeks 3-8, not 1-6.
 */
export function visibleReports(reports, limit) {
    if (limit === 'all' || reports.length <= limit)
        return [...reports];
    return reports.slice(reports.length - limit);
}
// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------
/** Short enough for a column header at print width: "10 Aug 26". */
export function formatColumnDate(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return '—';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
    });
}
/**
 * `periodStart` is the spine. `programmeWeek` is nullable context, so it is
 * appended when present and simply absent otherwise — a client with no
 * programme attached still gets a correctly labelled column.
 */
export function columnSubtitle(report) {
    return report.programmeWeek === null ? null : `Programme week ${report.programmeWeek}`;
}
// ---------------------------------------------------------------------------
// Ordering and edits
// ---------------------------------------------------------------------------
/**
 * Reports oldest first, by the week they are FOR.
 *
 * Ordering by `reportDate` instead would put a client who filled in a missed
 * week late after the week that followed it, and both surfaces read left to
 * right as a chronology. The API already orders this way; doing it here too is
 * cheap, and "there is nothing here in the right order" is a clinical claim
 * worth guaranteeing rather than trusting.
 */
export function orderReports(reports) {
    return [...reports].sort((a, b) => new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime());
}
/**
 * Anything under a minute is the same write: `reportDate` is stamped by the
 * service and `updatedAt` by the database, milliseconds apart, and calling that
 * an edit would mark every report ever submitted.
 */
const EDIT_THRESHOLD_MS = 60000;
/**
 * When a report was last changed, if that was meaningfully after it was sent.
 *
 * The coach needs this because editing is open indefinitely (#275) and there is
 * no revision history yet — that question is about auditing health records and
 * belongs with #258. Until then, "submitted 11 Aug, edited 14 Aug" is what stops
 * Christine silently reading numbers that moved after she wrote her notes.
 */
export function editedAt(report) {
    const submitted = new Date(report.reportDate).getTime();
    const updated = new Date(report.updatedAt).getTime();
    if (!Number.isFinite(submitted) || !Number.isFinite(updated))
        return null;
    return updated - submitted >= EDIT_THRESHOLD_MS ? new Date(updated) : null;
}
